import { useEffect, useState } from 'react';
import { Modal, Input, Button, Progress, Tag, Typography, Alert, Space } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';

const { Text } = Typography;

/**
 * Deliberately slow bulk delete: the admin steps through every selected
 * entity and must type its name exactly (no pasting) before moving on, then
 * confirms once more on a final summary screen that holds the only Delete
 * button.
 *
 *   <BulkDeleteModal
 *       open={bulkDeleteOpen}
 *       entities={selectedEntities}
 *       busy={bulkActionBusy}
 *       onCancel={() => setBulkDeleteOpen(false)}
 *       onConfirm={handleBulkDelete}      // returns a promise; modal closes after
 *   />
 */
function BulkDeleteModal({ open, entities, busy, onCancel, onConfirm }) {
    // 0..entities.length-1 = typing step for that entity; entities.length = final confirm.
    const [step, setStep] = useState(0);
    const [typed, setTyped] = useState('');

    // Fresh walk-through every time the modal opens.
    useEffect(() => {
        if (open) { setStep(0); setTyped(''); }
    }, [open]);

    const total = entities.length;
    const onFinal = step >= total;
    const current = entities[step];
    const matches = !!current && typed === current.name;
    const isClaimedPerson = e => e.entity_type === 'person' && !!e.user_id;

    const next = () => {
        if (!matches) return;
        setStep(s => s + 1);
        setTyped('');
    };
    const back = () => { setStep(s => Math.max(0, s - 1)); setTyped(''); };

    const footer = onFinal ? [
        <Button key="back" onClick={back} disabled={busy}>Back</Button>,
        <Button key="cancel" onClick={onCancel} disabled={busy}>Cancel</Button>,
        <Button key="delete" danger type="primary" icon={<DeleteOutlined />} loading={busy} onClick={onConfirm}>
            Delete {total} {total === 1 ? 'entity' : 'entities'}
        </Button>,
    ] : [
        <Button key="back" onClick={back} disabled={step === 0}>Back</Button>,
        <Button key="cancel" onClick={onCancel}>Cancel</Button>,
        <Button key="next" type="primary" danger disabled={!matches} onClick={next}>
            {step === total - 1 ? 'Review' : 'Next'}
        </Button>,
    ];

    return (
        <Modal
            title={`Delete ${total} ${total === 1 ? 'entity' : 'entities'}`}
            open={open}
            onCancel={busy ? undefined : onCancel}
            maskClosable={false}
            footer={footer}
            destroyOnHidden
        >
            <Progress
                percent={Math.round((Math.min(step, total) / Math.max(total, 1)) * 100)}
                showInfo={false}
                status="exception"
                size="small"
                style={{ marginBottom: 12 }}
            />

            {!onFinal && current && (
                <>
                    <Text type="secondary">Entity {step + 1} of {total}</Text>
                    <div style={{ margin: '8px 0 12px' }}>
                        <Space size={8} wrap>
                            <Text strong style={{ fontSize: 16 }}>{current.name}</Text>
                            <Tag style={{ textTransform: 'capitalize' }}>{current.entity_type}</Tag>
                        </Space>
                        {isClaimedPerson(current) && (
                            <div>
                                <Text type="danger" style={{ fontSize: 13 }}>
                                    This person has a login — their account is deleted too.
                                </Text>
                            </div>
                        )}
                    </div>
                    <Text>Type the name exactly to confirm:</Text>
                    <Input
                        autoFocus
                        key={current.id} // reset caret/IME state between entities
                        value={typed}
                        onChange={e => setTyped(e.target.value)}
                        onPaste={e => e.preventDefault()} // must be typed, not copied from above
                        onPressEnter={next}
                        status={typed && !matches ? 'error' : undefined}
                        placeholder={current.name}
                        autoComplete="off"
                        spellCheck={false}
                        style={{ marginTop: 6 }}
                    />
                    {typed && !matches && (
                        <Text type="danger" style={{ fontSize: 12 }}>Doesn't match — it must be exact, including case.</Text>
                    )}
                </>
            )}

            {onFinal && (
                <>
                    <Alert
                        type="error"
                        showIcon
                        style={{ marginBottom: 12 }}
                        title="This cannot be undone"
                        description={
                            entities.some(isClaimedPerson)
                                ? 'These entities will be permanently deleted. People with a login also lose their account.'
                                : 'These entities will be permanently deleted.'
                        }
                    />
                    <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                        {entities.map(e => (
                            <div key={e.id} style={{ padding: '2px 0' }}>
                                <Space size={8}>
                                    <Text>{e.name}</Text>
                                    <Tag style={{ textTransform: 'capitalize' }}>{e.entity_type}</Tag>
                                    {isClaimedPerson(e) && <Tag color="red">login deleted</Tag>}
                                </Space>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </Modal>
    );
}

export default BulkDeleteModal;
